/**
 * One list for the day, built from three sources that share almost nothing.
 *
 * Pure: no DOM, no fetch, no clock. Everything it needs arrives as an
 * argument, so every rule below is testable at the boundary that matters.
 *
 * ## The two joins, and why only these two
 *
 * The three sources are strangers. A block knows a local sqlite `workItemId`.
 * A Canvas assignment knows Canvas's own numeric `id`. A calendar event knows
 * a Google event id. None of them can be matched by looking at them; they can
 * only be matched by something that already knew both, and there are exactly
 * two such things:
 *
 * - `block.gcalEventId` -- Alter created that calendar event for that session
 *   and stored the id, so this is a fact rather than a guess.
 * - `block.canvasAssignmentId` -- the `work_item` row came from that Canvas
 *   assignment, and `dayplan.py` now publishes the link.
 *
 * Titles are deliberately not a join. They were never going to match ("Lab
 * report 3" vs "Lab Report 3 (PHYS 201)"), and the failure is asymmetric: a
 * missed match shows one thing twice, which is untidy, while a false match
 * hides a real commitment off the day, which is the kind of thing you find
 * out about afterwards.
 *
 * ## Units
 *
 * The three sources disagree about time itself. Blocks publish epoch
 * SECONDS, `shapeEvent` hands back a `Date`, and Canvas sends an ISO string.
 * Sorting those together untouched puts every block in 1970 -- sorted, no
 * error, entirely wrong. Everything is normalised to epoch milliseconds on
 * the way in, and `at` is always that.
 *
 * ## Keys
 *
 * A done-flag has to name something, and `workItemId` cannot be that name:
 * an assignment with no block has no `workItemId` the browser could ever
 * know, and those are exactly the rows worth ticking. So each row carries a
 * `key`, prefixed by which id space it came from -- `w118` and `c118` are
 * different pieces of work, and unprefixed they would tick each other.
 */

/** Today's rows, ordered by when they happen. */
export function mergeToday({ blocks, assignments, events, doneIds } = {}) {
  const plan = Array.isArray(blocks) ? blocks : [];
  const canvas = Array.isArray(assignments) ? assignments : [];
  const calendar = Array.isArray(events) ? events : [];
  const done = doneIds instanceof Set ? doneIds : new Set(doneIds ?? []);

  // Both joins are id sets built once. `null` is not an id: a block with no
  // gcal event and an event with no id must not match through a shared
  // absence, which a naive `includes(undefined)` would let happen.
  const scheduledEvents = new Set(
    plan.map((b) => b.gcalEventId).filter((id) => id !== null && id !== undefined),
  );
  const scheduledCanvas = new Set(
    plan.map((b) => b.canvasAssignmentId)
      .filter((id) => id !== null && id !== undefined)
      .map(String),
  );

  const rows = [];

  for (const b of plan) {
    const at = seconds(b.startAt);
    if (at === null) continue;
    rows.push(withDone({
      kind: 'block',
      source: 'plan',
      id: b.sessionId ?? null,
      sessionId: b.sessionId ?? null,
      workItemId: b.workItemId ?? null,
      canvasAssignmentId: b.canvasAssignmentId ?? null,
      title: b.title ?? '',
      courseCode: b.courseCode ?? '',
      plannedMinutes: b.plannedMinutes ?? null,
      // How long this block has actually been worked, as the desktop counts
      // it. Published by `alter/dayplan.py`; drawn here so the phone can
      // answer "how far in am I" without opening the laptop.
      workedMinutes: b.workedMinutes ?? 0,
      allDay: false,
      at,
    }, done));
  }

  for (const a of canvas) {
    // Shown only when nothing in the plan already speaks for it.
    if (a.id !== null && a.id !== undefined && scheduledCanvas.has(String(a.id))) continue;
    const at = isoTime(a.due);
    if (at === null) continue;
    const row = withDone({
      kind: 'assignment',
      source: 'canvas',
      id: a.id ?? null,
      sessionId: null,
      // The browser has no way to know this. Only the desktop holds the
      // mapping, and it only publishes it for work it actually scheduled.
      workItemId: null,
      canvasAssignmentId: a.id === null || a.id === undefined ? null : String(a.id),
      title: a.title ?? '',
      courseCode: '',
      plannedMinutes: null,
      workedMinutes: 0,
      url: a.url ?? '',
      allDay: false,
      at,
    }, done);
    // Canvas is the authority on its own submissions, so a submitted
    // assignment is done on first paint rather than after a round trip.
    if (a.submitted) row.done = true;
    rows.push(row);
  }

  for (const e of calendar) {
    if (e.id !== null && e.id !== undefined && scheduledEvents.has(e.id)) continue;
    const at = eventTime(e);
    if (at === null) continue;
    rows.push(withDone({
      kind: 'event',
      source: 'calendar',
      id: e.id ?? null,
      sessionId: null,
      workItemId: null,
      canvasAssignmentId: null,
      title: e.title ?? '',
      courseCode: e.calendarName ?? '',
      plannedMinutes: null,
      workedMinutes: 0,
      location: e.location ?? '',
      allDay: Boolean(e.allDay),
      at,
    }, done));
  }

  rows.sort((x, y) => x.at - y.at);
  return rows;
}

/**
 * What a done-flag for this row is called.
 *
 * Prefixed by id space on purpose: work item 118 and Canvas assignment 118
 * are different pieces of work, and an unprefixed key would let ticking one
 * tick the other -- silently, and only for the pairs that happen to collide.
 */
export function itemKey(row) {
  if (!row) return null;
  if (row.kind === 'block') {
    return row.workItemId === null || row.workItemId === undefined
      ? null
      : `w${row.workItemId}`;
  }
  if (row.kind === 'assignment') {
    return row.id === null || row.id === undefined ? null : `c${row.id}`;
  }
  return row.id ?? null;
}

/**
 * Every key this row could have been ticked under.
 *
 * A Canvas assignment ticked while unscheduled is flagged `c90210`. If Alter
 * then schedules it, the same work arrives as a block whose key is `w118`,
 * and checking only the block's own key would quietly untick it. Both are
 * checked, so the tick survives being scheduled.
 */
function keysFor(row) {
  const keys = [];
  const own = itemKey(row);
  if (own !== null) keys.push(own);
  if (row.kind === 'block' && row.canvasAssignmentId) keys.push(`c${row.canvasAssignmentId}`);
  return keys;
}

function withDone(row, done) {
  const key = itemKey(row);
  return { ...row, key, done: keysFor(row).some((k) => done.has(k)) };
}

/** Epoch seconds to epoch milliseconds, or null if it is not a time. */
function seconds(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return value * 1000;
}

function isoTime(value) {
  if (typeof value !== 'string') return null;
  const at = Date.parse(value);
  return Number.isNaN(at) ? null : at;
}

/**
 * When an event sits in the day.
 *
 * `shapeEvent` gives an all-day event `start: null` and only a `startDay`, so
 * reading `.start.getTime()` throws on precisely the events people put their
 * whole day into. Local midnight puts them at the top of the list, which is
 * also where they belong.
 */
function eventTime(e) {
  if (e.start instanceof Date && !Number.isNaN(e.start.getTime())) return e.start.getTime();
  if (typeof e.startDay === 'string') {
    const parts = e.startDay.split('-').map(Number);
    if (parts.length === 3 && parts.every((n) => Number.isFinite(n))) {
      // Built from parts, not `Date.parse`, which reads a bare `YYYY-MM-DD`
      // as UTC and lands the previous evening for anyone west of Greenwich.
      return new Date(parts[0], parts[1] - 1, parts[2]).getTime();
    }
  }
  return null;
}
